/**
 * FollowUpHistoryTable
 * Cards no mobile, tabela no desktop — filtrada por status (Programados/Pausados/
 * Enviados/Falhas). A primeira coluna de data muda conforme o status ativo.
 */
import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../../../components/ui/table';
import { ClockIcon } from '../../../../components/icons';
import { formatDateTime, formatPhoneWithDDI } from '../../../../utils/formatters';
import type { FollowUpJob } from '../types';
import type { FollowUpHistoryStatus } from '../hooks/useFollowUpHistory';

interface FollowUpHistoryTableProps {
    status: FollowUpHistoryStatus;
    rows: FollowUpJob[];
    loading: boolean;
}

const DATE_COLUMN_LABEL: Record<FollowUpHistoryStatus, string> = {
    scheduled: 'Data prevista',
    paused: 'Pausado até',
    sent: 'Enviado em',
    failed: 'Data prevista',
};

const EMPTY_STATE_MESSAGE: Record<FollowUpHistoryStatus, string> = {
    scheduled: 'Nenhum follow-up agendado para este período.',
    paused: 'Nenhum follow-up pausado para este período.',
    sent: 'Nenhum follow-up enviado para este período.',
    failed: 'Nenhuma falha de envio para este período.',
};

function renderDateCell(job: FollowUpJob, status: FollowUpHistoryStatus): string {
    if (status === 'paused' && job.pauseUntil) {
        return `até ${formatDateTime(job.pauseUntil)}`;
    }

    const value = status === 'sent' ? job.sentAt ?? job.scheduledAt : job.scheduledAt;
    return formatDateTime(value);
}

function renderStatusCell(job: FollowUpJob) {
    if (job.status === 'sent') {
        return <span className="badge badge-sm badge-success">Enviado</span>;
    }

    if (job.status === 'failed') {
        return (
            <div className="flex flex-col gap-s1">
                <span className="text-sm font-medium text-error">Falhou</span>
                {job.failureReason && (
                    <span className="text-xs text-secondary">{job.failureReason}</span>
                )}
            </div>
        );
    }

    if (job.status === 'paused') {
        return <span className="badge badge-sm badge-warning">Pausado</span>;
    }

    return <span className="badge badge-sm badge-neutral">Agendado</span>;
}

function ContactBlock({ job }: { job: FollowUpJob }) {
    return (
        <div className="flex min-w-0 flex-col gap-s1">
            <span className="truncate text-sm font-medium text-default">
                {job.contactName || 'Contato sem nome'}
            </span>
            <span className="font-mono text-xs tabular-nums text-secondary">
                {job.contactPhone ? formatPhoneWithDDI(job.contactPhone) : '—'}
            </span>
        </div>
    );
}

const FollowUpHistoryTable: React.FC<FollowUpHistoryTableProps> = ({
    status,
    rows,
    loading,
}) => {
    if (loading) {
        return (
            <div className="px-s6 py-s8 text-sm text-secondary" role="status" aria-busy="true">
                Carregando histórico...
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="flex flex-col items-center gap-s2 px-s6 py-s10 text-center">
                <div className="flex size-11 items-center justify-center rounded-xl bg-surface-subtle text-secondary">
                    <ClockIcon className="size-5" aria-hidden="true" />
                </div>
                <p className="text-sm font-medium text-default">{EMPTY_STATE_MESSAGE[status]}</p>
                <p className="text-sm text-secondary">Ajuste o período ou o status para ver outros resultados.</p>
            </div>
        );
    }

    return (
        <>
            {/* Mobile: cards */}
            <ul className="flex flex-col gap-s3 md:hidden" aria-label="Histórico de follow-ups">
                {rows.map((job) => (
                    <li key={job.id}>
                        <article className="rounded-xl border border-border bg-surface p-s4">
                            <div className="flex items-start justify-between gap-s3">
                                <ContactBlock job={job} />
                                {renderStatusCell(job)}
                            </div>
                            <dl className="mt-s3 grid grid-cols-2 gap-s3 text-xs">
                                <div>
                                    <dt className="text-muted">{DATE_COLUMN_LABEL[status]}</dt>
                                    <dd className="mt-s1 font-mono tabular-nums text-secondary">
                                        {renderDateCell(job, status)}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-muted">Follow-up</dt>
                                    <dd className="mt-s1 text-secondary">{job.delayLabel ?? '—'}</dd>
                                </div>
                                <div className="col-span-2">
                                    <dt className="text-muted">Negociação</dt>
                                    <dd className="mt-s1 line-clamp-2 text-secondary">
                                        {job.opportunityTitle || 'Negociação sem título'}
                                    </dd>
                                </div>
                            </dl>
                        </article>
                    </li>
                ))}
            </ul>

            {/* Desktop: tabela */}
            <div className="hidden overflow-x-auto md:block">
                <Table className="min-w-[40rem]">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{DATE_COLUMN_LABEL[status]}</TableHead>
                            <TableHead>Contato</TableHead>
                            <TableHead>Follow-up</TableHead>
                            <TableHead>Negociação</TableHead>
                            <TableHead>Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((job) => (
                            <TableRow key={job.id}>
                                <TableCell className="font-mono text-xs tabular-nums text-secondary whitespace-nowrap">
                                    {renderDateCell(job, status)}
                                </TableCell>
                                <TableCell>
                                    <ContactBlock job={job} />
                                </TableCell>
                                <TableCell className="text-default">
                                    {job.delayLabel ?? '—'}
                                </TableCell>
                                <TableCell className="max-w-56">
                                    <span className="line-clamp-2 text-sm text-default" title={job.opportunityTitle ?? undefined}>
                                        {job.opportunityTitle || 'Negociação sem título'}
                                    </span>
                                </TableCell>
                                <TableCell>{renderStatusCell(job)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </>
    );
};

export default FollowUpHistoryTable;
